import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { fetchCvs, uploadCv, updateCvStatus, deleteCv, anonymiseCv, downloadCv, syncCvsFromOneDrive, assignOneDriveFile, fetchUnmatchedFiles, reassignCv, extractCvSkillsTaxonomy } from '../api/cvs';
import { fetchEmployees } from '../api/employees';
import type { EmployeeListItem } from '../api/employees';
import type { CvMetadata, CvStatus } from '../types';
import type { OneDriveSyncResult, UnmatchedFile } from '../api/cvs';
import StatusBadge from '../components/StatusBadge';
import LocalFolderStatus from '../components/LocalFolderStatus';

const ALLOWED_MIME = ['application/pdf', 'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
const MAX_FILE_SIZE_MB = 10;

export default function CvManagement() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage === 'de' ? 'de-DE' : 'en-GB';

  const [cvs, setCvs] = useState<CvMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CvStatus | ''>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Upload state
  const [uploadEmail, setUploadEmail] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [extractMessage, setExtractMessage] = useState<string | null>(null);

  // OneDrive sync state
  const [oneDriveSyncing, setOneDriveSyncing] = useState(false);
  const [oneDriveSyncResult, setOneDriveSyncResult] = useState<OneDriveSyncResult | null>(null);
  const [oneDriveSyncError, setOneDriveSyncError] = useState<string | null>(null);
  const [unmatchedFiles, setUnmatchedFiles] = useState<UnmatchedFile[]>([]);
  const [assignSelections, setAssignSelections] = useState<Record<string, string>>({});
  const [assigningFile, setAssigningFile] = useState<string | null>(null);
  const [employeeOptions, setEmployeeOptions] = useState<EmployeeListItem[]>([]);

  // Reassign state
  const [reassigningCvId, setReassigningCvId] = useState<string | null>(null);
  const [reassignSelections, setReassignSelections] = useState<Record<string, string>>({});

  const loadCvs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchCvs({
        page,
        page_size: 20,
        search: search || undefined,
        status: statusFilter || undefined,
        sort_by: 'upload_date',
        order: 'desc',
      });
      setCvs(result.items);
      setTotalPages(result.total_pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('cvManagement.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, t]);

  const loadEmployeeOptions = useCallback(async () => {
    try {
      const empResult = await fetchEmployees({ page: 1, page_size: 500, status: 'ACTIVE', sort_by: 'fullName', order: 'asc' });
      setEmployeeOptions(empResult.items);
    } catch {
      // non-critical
    }
  }, []);

  const loadUnmatchedFiles = useCallback(async () => {
    try {
      const files = await fetchUnmatchedFiles();
      setUnmatchedFiles(files);
    } catch {
      // Silently ignore — unmatched files are a secondary concern
    }
  }, []);

  useEffect(() => {
    loadCvs();
    loadUnmatchedFiles();
    loadEmployeeOptions();
  }, [loadCvs, loadUnmatchedFiles, loadEmployeeOptions]);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setUploadFile(null);
    setUploadError(null);
    if (!file) return;

    if (!ALLOWED_MIME.includes(file.type)) {
      setUploadError(t('cvManagement.onlyPdfWord'));
      return;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setUploadError(t('cvManagement.fileTooLarge', { maxSize: MAX_FILE_SIZE_MB }));
      return;
    }
    setUploadFile(file);
  }

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    if (!uploadFile || !uploadEmail.trim()) return;
    setUploadProgress(0);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      const result = await uploadCv(uploadEmail.trim(), uploadFile, setUploadProgress);
      setUploadSuccess(
        t('cvManagement.uploadSuccess', { count: result.extracted_skills.length })
      );
      setUploadFile(null);
      setUploadEmail('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadCvs();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : t('cvManagement.uploadFailed'));
    } finally {
      setUploadProgress(null);
    }
  }

  async function handleOneDriveSync() {
    setOneDriveSyncing(true);
    setOneDriveSyncResult(null);
    setOneDriveSyncError(null);
    try {
      const result = await syncCvsFromOneDrive();
      setOneDriveSyncResult(result);
      await loadCvs();
      await loadUnmatchedFiles();
    } catch (err) {
      setOneDriveSyncError(err instanceof Error ? err.message : t('cvManagement.oneDriveFailed', { error: '' }));
    } finally {
      setOneDriveSyncing(false);
    }
  }

  async function handleAssignFile(file: UnmatchedFile) {
    const employeeId = assignSelections[file.item_id];
    if (!employeeId) return;
    setAssigningFile(file.item_id);
    try {
      await assignOneDriveFile(file.item_id, file.file_name, employeeId);
      setUnmatchedFiles((prev) => prev.filter((f) => f.item_id !== file.item_id));
      await loadCvs();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('cvManagement.assignFailed'));
    } finally {
      setAssigningFile(null);
    }
  }

  async function handleReassign(cvId: string) {
    const employeeId = reassignSelections[cvId];
    if (!employeeId) return;
    setActionLoading(cvId);
    try {
      await reassignCv(cvId, employeeId);
      setReassigningCvId(null);
      setReassignSelections((prev) => { const next = { ...prev }; delete next[cvId]; return next; });
      await loadCvs();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('cvManagement.reassignFailed'));
    } finally {
      setActionLoading(null);
    }
  }

  async function handleExtractSkills(cvId: string, employeeName: string) {
    if (!window.confirm(t('cvManagement.confirmExtractSkills'))) return;
    setActionLoading(cvId);
    setExtractMessage(null);
    try {
      const result = await extractCvSkillsTaxonomy(cvId);
      setExtractMessage(
        t('cvManagement.extractSkillsSuccess', {
          name: employeeName,
          resolved: result.resolved,
          observed: result.observed,
        })
      );
    } catch (err) {
      setExtractMessage(err instanceof Error ? err.message : t('cvManagement.extractSkillsFailed'));
    } finally {
      setActionLoading(null);
    }
  }

  async function handleStatusChange(cvId: string, status: CvStatus) {
    setActionLoading(cvId);
    try {
      await updateCvStatus(cvId, status);
      await loadCvs();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('cvManagement.statusUpdateFailed'));
    } finally {
      setActionLoading(null);
    }
  }

  async function handleAnonymise(cvId: string, employeeName: string) {
    if (!window.confirm(t('cvManagement.confirmAnonymise', { name: employeeName }))) return;
    setActionLoading(cvId);
    try {
      await anonymiseCv(cvId);
      await loadCvs();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('cvManagement.anonymiseFailed'));
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDelete(cvId: string, employeeName: string) {
    if (!window.confirm(t('cvManagement.confirmDelete', { name: employeeName }))) return;
    setActionLoading(cvId);
    try {
      await deleteCv(cvId);
      await loadCvs();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('cvManagement.deleteFailed'));
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDownload(cvId: string, fileName: string) {
    try {
      const blob = await downloadCv(cvId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err instanceof Error ? err.message : t('cvManagement.downloadFailed'));
    }
  }

  function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-bold text-gray-900">{t('cvManagement.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('cvManagement.subtitle')}
        </p>
      </header>

      {/* Local Folder Ingestion */}
      <LocalFolderStatus />

      {/* OneDrive CV Import */}
      <section className="card flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold text-gray-900">{t('cvManagement.oneDriveImport')}</h2>
          <p className="text-xs text-gray-500">
            {t('cvManagement.oneDriveImportHint')}
          </p>
        </div>
        <button
          onClick={handleOneDriveSync}
          disabled={oneDriveSyncing}
          className="btn-primary text-sm"
          aria-busy={oneDriveSyncing}
        >
          {oneDriveSyncing ? t('cvManagement.importing') : t('cvManagement.importFromOneDrive')}
        </button>
      </section>

      {oneDriveSyncResult && (
        <div role="status" aria-live="polite" className="rounded bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
          {t('cvManagement.oneDriveComplete', {
            total: oneDriveSyncResult.total_files,
            ingested: oneDriveSyncResult.ingested,
            duplicate: oneDriveSyncResult.skipped_duplicate,
            noMatch: oneDriveSyncResult.skipped_no_match,
            failed: oneDriveSyncResult.failed,
          })}
        </div>
      )}

      {oneDriveSyncError && (
        <div role="alert" className="rounded bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {t('cvManagement.oneDriveFailed', { error: oneDriveSyncError })}
        </div>
      )}

      {/* Unmatched files — manual assignment */}
      {unmatchedFiles.length > 0 && (
        <section className="card space-y-3">
          <h2 className="text-sm font-semibold text-gray-900">
            {t('cvManagement.unmatchedFiles', { count: unmatchedFiles.length })}
          </h2>
          <p className="text-xs text-gray-500">
            {t('cvManagement.unmatchedFilesHint')}
          </p>
          <div className="overflow-x-auto">
            <table className="data-table" aria-label={t('cvManagement.unmatchedTable')}>
              <thead>
                <tr>
                  <th scope="col">{t('cvManagement.fileName')}</th>
                  <th scope="col">{t('cvManagement.size')}</th>
                  <th scope="col">{t('cvManagement.assignToEmployee')}</th>
                  <th scope="col"><span className="sr-only">{t('common.actions')}</span></th>
                </tr>
              </thead>
              <tbody>
                {unmatchedFiles.map((file) => (
                  <tr key={file.item_id}>
                    <td className="text-sm text-gray-900">{file.file_name}</td>
                    <td className="text-xs text-gray-500">
                      {file.size < 1024 * 1024
                        ? `${(file.size / 1024).toFixed(1)} KB`
                        : `${(file.size / (1024 * 1024)).toFixed(1)} MB`}
                    </td>
                    <td>
                      <Select
                        options={employeeOptions.map((emp) => ({ value: emp.id, label: `${emp.full_name} (${emp.email})` }))}
                        value={assignSelections[file.item_id] ? { value: assignSelections[file.item_id], label: employeeOptions.find((e) => e.id === assignSelections[file.item_id])?.full_name ?? '' } : null}
                        onChange={(opt) => setAssignSelections((prev) => ({ ...prev, [file.item_id]: opt?.value ?? '' }))}
                        placeholder={t('common.selectEmployee')}
                        isClearable
                        classNamePrefix="react-select"
                        aria-label={t('cvManagement.assignLabel', { fileName: file.file_name })}
                        styles={{
                          control: (base) => ({ ...base, fontSize: '0.875rem', minHeight: '34px', minWidth: '200px' }),
                          option: (base) => ({ ...base, fontSize: '0.875rem' }),
                        }}
                      />
                    </td>
                    <td>
                      <button
                        onClick={() => handleAssignFile(file)}
                        disabled={!assignSelections[file.item_id] || assigningFile === file.item_id}
                        className="btn-primary text-xs py-1 px-2"
                        aria-busy={assigningFile === file.item_id}
                      >
                        {assigningFile === file.item_id ? t('cvManagement.assigning') : t('cvManagement.assign')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Upload section */}
      <section aria-label={t('cvManagement.uploadCv')} className="card space-y-4">
        <h2 className="text-base font-semibold text-gray-900">{t('cvManagement.uploadCv')}</h2>

        {uploadSuccess && (
          <div role="status" aria-live="polite" className="rounded bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
            {uploadSuccess}
          </div>
        )}

        <form onSubmit={handleUpload} noValidate aria-label={t('cvManagement.cvUploadForm')} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="employee-email" className="form-label">
                {t('cvManagement.employeeEmail')} <span className="text-red-500" aria-hidden="true">*</span>
              </label>
              <input
                id="employee-email"
                type="email"
                value={uploadEmail}
                onChange={(e) => setUploadEmail(e.target.value)}
                required
                aria-required="true"
                placeholder="e.g. jane.doe@company.com"
                className="form-input"
              />
            </div>

            <div>
              <label htmlFor="cv-file" className="form-label">
                {t('cvManagement.cvFile', { maxSize: MAX_FILE_SIZE_MB })}{' '}
                <span className="text-red-500" aria-hidden="true">*</span>
              </label>
              <input
                id="cv-file"
                type="file"
                ref={fileInputRef}
                accept=".pdf,.doc,.docx"
                required
                aria-required="true"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-600 file:mr-3 file:py-1 file:px-3 file:rounded file:border file:border-gray-300 file:text-xs file:bg-white file:text-gray-700 hover:file:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              />
            </div>
          </div>

          {uploadError && (
            <p role="alert" className="text-sm text-red-600">{uploadError}</p>
          )}

          {uploadProgress !== null && (
            <div aria-label={t('cvManagement.uploadProgress', { progress: uploadProgress })} className="space-y-1">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand-600 transition-all duration-200"
                  style={{ width: `${uploadProgress}%` }}
                  role="progressbar"
                  aria-valuenow={uploadProgress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
              <p className="text-xs text-gray-500">{t('cvManagement.percentUploaded', { progress: uploadProgress })}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={!uploadFile || !uploadEmail.trim() || uploadProgress !== null}
            className="btn-primary"
            aria-busy={uploadProgress !== null}
          >
            {uploadProgress !== null ? t('cvManagement.uploadingCv') : t('cvManagement.uploadCvBtn')}
          </button>
        </form>
      </section>

      {/* Filters */}
      <section aria-label={t('cvManagement.cvFilters')} className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-48">
          <label htmlFor="cv-search" className="sr-only">{t('cvManagement.searchLabel')}</label>
          <input
            id="cv-search"
            type="search"
            placeholder={t('cvManagement.searchPlaceholder')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="form-input"
          />
        </div>
        <div>
          <label htmlFor="cv-status-filter" className="sr-only">{t('cvManagement.filterByStatus')}</label>
          <Select
            aria-labelledby="cv-status-filter"
            options={[
              { value: '', label: t('cvManagement.allStatuses') },
              { value: 'active', label: t('cvManagement.active') },
              { value: 'inactive', label: t('cvManagement.inactive') },
              { value: 'pending_review', label: t('cvManagement.pendingReview') },
            ]}
            value={{
              value: statusFilter,
              label: statusFilter === 'active' ? t('cvManagement.active') : statusFilter === 'inactive' ? t('cvManagement.inactive') : statusFilter === 'pending_review' ? t('cvManagement.pendingReview') : t('cvManagement.allStatuses'),
            }}
            onChange={(opt) => { setStatusFilter((opt?.value ?? '') as CvStatus | ''); setPage(1); }}
            classNamePrefix="react-select"
            isSearchable={false}
            styles={{
              control: (base) => ({ ...base, fontSize: '0.875rem', minHeight: '38px', minWidth: '180px' }),
              option: (base) => ({ ...base, fontSize: '0.875rem' }),
            }}
          />
        </div>
        <button onClick={loadCvs} className="btn-secondary">{t('common.refresh')}</button>
      </section>

      {/* Skill extraction result */}
      {extractMessage && (
        <div role="status" aria-live="polite" className="rounded bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-700 flex items-start justify-between gap-3">
          <span>{extractMessage}</span>
          <button
            onClick={() => setExtractMessage(null)}
            className="text-blue-400 hover:text-blue-700 font-semibold leading-none"
            aria-label={t('common.dismiss')}
          >
            ×
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div role="alert" className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div role="status" aria-live="polite" className="flex items-center gap-3 text-gray-500 py-8">
          <span className="animate-spin text-brand-600 text-xl" aria-hidden="true">⟳</span>
          {t('cvManagement.loading')}
        </div>
      )}

      {/* CV table */}
      {!loading && !error && (
        <>
          {cvs.length === 0 ? (
            <div className="card text-center py-12 text-gray-500 text-sm">
              {t('cvManagement.noCvs')}
            </div>
          ) : (
            <div className="card overflow-hidden p-0">
              <div className="overflow-x-auto">
                <table className="data-table" aria-label={t('cvManagement.cvList')}>
                  <thead>
                    <tr>
                      <th scope="col">{t('cvManagement.employee')}</th>
                      <th scope="col">{t('cvManagement.file')}</th>
                      <th scope="col">{t('cvManagement.uploaded')}</th>
                      <th scope="col">{t('cvManagement.size')}</th>
                      <th scope="col">{t('cvManagement.skillsExtracted')}</th>
                      <th scope="col">{t('common.status')}</th>
                      <th scope="col">{t('cvManagement.anonymised')}</th>
                      <th scope="col"><span className="sr-only">{t('common.actions')}</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {cvs.map((cv) => (
                      <tr key={cv.id}>
                        <td className="font-medium text-gray-900">{cv.employee_name}</td>
                        <td className="text-gray-600 max-w-[200px] truncate text-xs">{cv.file_name}</td>
                        <td>
                          <time dateTime={cv.upload_date}>
                            {new Date(cv.upload_date).toLocaleDateString(dateLocale, {
                              day: '2-digit', month: 'short', year: 'numeric',
                            })}
                          </time>
                        </td>
                        <td className="text-xs text-gray-500">{formatBytes(cv.file_size)}</td>
                        <td>
                          <span className="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
                            {t('cvManagement.skillsCount', { count: cv.extracted_skills.length })}
                          </span>
                        </td>
                        <td>
                          <StatusBadge status={cv.status} />
                        </td>
                        <td>
                          {cv.anonymized ? (
                            <span className="text-xs text-green-600 font-medium">{t('common.yes')}</span>
                          ) : (
                            <span className="text-xs text-gray-400">{t('common.no')}</span>
                          )}
                        </td>
                        <td>
                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            <button
                              onClick={() => handleDownload(cv.id, cv.file_name)}
                              disabled={actionLoading === cv.id}
                              className="text-brand-600 hover:text-brand-800 disabled:opacity-40 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-500 rounded"
                              aria-label={t('cvManagement.downloadCv', { name: cv.employee_name })}
                            >
                              {t('cvManagement.download')}
                            </button>
                            <button
                              onClick={() => handleExtractSkills(cv.id, cv.employee_name)}
                              disabled={actionLoading === cv.id}
                              className="text-teal-600 hover:text-teal-800 disabled:opacity-40 focus:outline-none focus-visible:ring-1 focus-visible:ring-teal-500 rounded"
                              aria-label={t('cvManagement.extractSkillsCv', { name: cv.employee_name })}
                            >
                              {actionLoading === cv.id ? t('cvManagement.extractingSkills') : t('cvManagement.extractSkills')}
                            </button>
                            {reassigningCvId === cv.id ? (
                              <span className="inline-flex items-center gap-1">
                                <Select
                                  options={employeeOptions
                                    .filter((emp) => emp.id !== cv.employee_id)
                                    .map((emp) => ({ value: emp.id, label: emp.full_name }))}
                                  value={reassignSelections[cv.id] ? { value: reassignSelections[cv.id], label: employeeOptions.find((e) => e.id === reassignSelections[cv.id])?.full_name ?? '' } : null}
                                  onChange={(opt) => setReassignSelections((prev) => ({ ...prev, [cv.id]: opt?.value ?? '' }))}
                                  placeholder={t('common.select')}
                                  isClearable
                                  classNamePrefix="react-select"
                                  aria-label={t('cvManagement.reassignLabel')}
                                  styles={{
                                    control: (base) => ({ ...base, fontSize: '0.75rem', minHeight: '28px', minWidth: '180px' }),
                                    option: (base) => ({ ...base, fontSize: '0.75rem' }),
                                    dropdownIndicator: (base) => ({ ...base, padding: '2px' }),
                                    clearIndicator: (base) => ({ ...base, padding: '2px' }),
                                  }}
                                />
                                <button
                                  onClick={() => handleReassign(cv.id)}
                                  disabled={!reassignSelections[cv.id] || actionLoading === cv.id}
                                  className="text-green-600 hover:text-green-800 disabled:opacity-40 focus:outline-none focus-visible:ring-1 focus-visible:ring-green-500 rounded"
                                  aria-label={t('cvManagement.confirmReassign')}
                                >
                                  {t('common.save')}
                                </button>
                                <button
                                  onClick={() => setReassigningCvId(null)}
                                  className="text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-400 rounded"
                                  aria-label={t('cvManagement.cancelReassign')}
                                >
                                  {t('common.cancel')}
                                </button>
                              </span>
                            ) : (
                              <button
                                onClick={() => setReassigningCvId(cv.id)}
                                disabled={actionLoading === cv.id}
                                className="text-blue-600 hover:text-blue-800 disabled:opacity-40 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 rounded"
                                aria-label={t('cvManagement.reassignCv', { name: cv.employee_name })}
                              >
                                {t('cvManagement.reassign')}
                              </button>
                            )}
                            {cv.status === 'active' ? (
                              <button
                                onClick={() => handleStatusChange(cv.id, 'inactive')}
                                disabled={actionLoading === cv.id}
                                className="text-amber-600 hover:text-amber-800 disabled:opacity-40 focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-500 rounded"
                                aria-label={t('cvManagement.deactivateCv', { name: cv.employee_name })}
                              >
                                {t('cvManagement.deactivate')}
                              </button>
                            ) : (
                              <button
                                onClick={() => handleStatusChange(cv.id, 'active')}
                                disabled={actionLoading === cv.id}
                                className="text-green-600 hover:text-green-800 disabled:opacity-40 focus:outline-none focus-visible:ring-1 focus-visible:ring-green-500 rounded"
                                aria-label={t('cvManagement.activateCv', { name: cv.employee_name })}
                              >
                                {t('cvManagement.activate')}
                              </button>
                            )}
                            {!cv.anonymized && (
                              <button
                                onClick={() => handleAnonymise(cv.id, cv.employee_name)}
                                disabled={actionLoading === cv.id}
                                className="text-purple-600 hover:text-purple-800 disabled:opacity-40 focus:outline-none focus-visible:ring-1 focus-visible:ring-purple-500 rounded"
                                aria-label={t('cvManagement.anonymiseCv', { name: cv.employee_name })}
                              >
                                {t('cvManagement.anonymise')}
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(cv.id, cv.employee_name)}
                              disabled={actionLoading === cv.id}
                              className="text-red-600 hover:text-red-800 disabled:opacity-40 focus:outline-none focus-visible:ring-1 focus-visible:ring-red-500 rounded"
                              aria-label={t('cvManagement.deleteCv', { name: cv.employee_name })}
                            >
                              {t('common.delete')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <nav aria-label={t('cvManagement.pagination')} className="flex items-center justify-center gap-3">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary"
                aria-label={t('common.previous')}
              >
                &larr; {t('common.previous')}
              </button>
              <span className="text-sm text-gray-600" aria-current="page">
                {t('common.pageOf', { page, totalPages })}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn-secondary"
                aria-label={t('common.next')}
              >
                {t('common.next')} &rarr;
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
